import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { maintenanceAPI, workerAPI } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { getRequiredSpecializations } from "../../utils/maintenanceUtils";

const AdminMaintenancePanel = () => {
  const { logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    assigned: 0,
    inProgress: 0,
    resolved: 0,
    rejected: 0,
  });

  const [assignmentForm, setAssignmentForm] = useState({
    workerId: "",
    scheduledDate: "",
    estimatedDuration: "",
    adminNotes: "",
  });

  useEffect(() => {
    fetchRequests();
    fetchWorkers();
    fetchStats();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await maintenanceAPI.getAllRequests();
      setRequests(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching maintenance requests:", error);
      setError("Failed to load maintenance requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      console.log("Fetching available workers...");
      const response = await workerAPI.getAvailableWorkers();
      console.log("Workers fetched:", response.data);
      setWorkers(response.data);
    } catch (error) {
      console.error("Error fetching workers:", error);
      // Try alternative endpoint if available workers fails
      try {
        console.log("Trying alternative workers endpoint...");
        const response = await workerAPI.getAllWorkers();
        console.log("Workers fetched via getAllWorkers:", response.data);
        setWorkers(response.data);
      } catch (fallbackError) {
        console.error("Error fetching workers (fallback):", fallbackError);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await maintenanceAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialog(true);
  };

  const handleAssignWorker = async (request) => {
    setSelectedRequest(request);
    setAssignDialog(true);
    setAssignmentForm({
      workerId: "",
      scheduledDate: "",
      estimatedDuration: "",
      adminNotes: "",
    });

    // Fetch workers filtered by required specialization for this category
    try {
      const requiredSpecializations = getRequiredSpecializations(
        request.category
      );
      console.log(
        `Fetching workers for category: ${request.category}, required specializations:`,
        requiredSpecializations
      );

      // Try to fetch workers for each required specialization
      let filteredWorkers = [];
      for (const specialization of requiredSpecializations) {
        try {
          const response = await workerAPI.getAvailableWorkers({
            specialization,
          });
          filteredWorkers = [...filteredWorkers, ...response.data];
        } catch (error) {
          console.error(
            `Error fetching workers for specialization ${specialization}:`,
            error
          );
        }
      }

      // Remove duplicates
      const uniqueWorkers = filteredWorkers.filter(
        (worker, index, self) =>
          index === self.findIndex((w) => w.id === worker.id)
      );

      if (uniqueWorkers.length > 0) {
        setWorkers(uniqueWorkers);
      } else {
        // Fallback to all available workers if no specialized workers found
        console.log(
          "No specialized workers found, falling back to all available workers"
        );
        await fetchWorkers();
      }
    } catch (error) {
      console.error("Error fetching specialized workers:", error);
      // Fallback to existing workers
      await fetchWorkers();
    }
  };

  const handleSubmitAssignment = async () => {
    console.log("Assignment form data:", assignmentForm);
    console.log("Selected request:", selectedRequest);

    if (!selectedRequest || !assignmentForm.workerId) {
      console.error("Assignment validation failed:", {
        hasSelectedRequest: !!selectedRequest,
        workerId: assignmentForm.workerId,
      });
      setError("Please select a worker");
      return;
    }

    setAssignLoading(true);
    try {
      console.log("Submitting assignment:", {
        requestId: selectedRequest.id,
        assignmentData: assignmentForm,
      });

      await maintenanceAPI.assignWorker(selectedRequest.id, assignmentForm);
      setAssignDialog(false);
      setSelectedRequest(null);
      await fetchRequests();
      await fetchStats();
      setError("");
    } catch (error) {
      console.error("Error assigning worker:", error);
      setError(
        `Failed to assign worker: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setAssignLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "assigned":
        return "info";
      case "in_progress":
        return "primary";
      case "resolved":
        return "success";
      case "rejected":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "assigned":
        return "Assigned";
      case "in_progress":
        return "In Progress";
      case "resolved":
        return "Resolved";
      case "rejected":
        return "Rejected";
      case "closed":
        return "Closed";
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Maintenance Administration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Manage maintenance requests and worker assignments
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={() => window.location.reload()}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton
              onClick={logout}
              color="error"
              sx={{
                border: 1,
                borderColor: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "white",
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <DashboardIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Total</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AssignmentIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Assigned</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {stats.assigned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">In Progress</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Resolved</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {stats.resolved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CancelIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">Rejected</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {stats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Requests Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token #</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Subcategory</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Requester</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Worker</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No maintenance requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {request.tokenNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {request.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {request.subcategory?.replace(/_/g, " ") || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.requesterName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority}
                        color={getPriorityColor(request.priority)}
                        size="small"
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(request.status)}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography variant="body2">
                          {request.assignedWorkerName || "Not assigned"}
                        </Typography>
                        {request.assignedWorkerName && request.autoAssigned && (
                          <Chip
                            label="Auto-assigned"
                            color="info"
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.5, fontSize: "0.7rem", height: "18px" }}
                          />
                        )}
                        {request.assignedWorkerName &&
                          request.manuallyOverridden && (
                            <Chip
                              label="Manager Override"
                              color="warning"
                              size="small"
                              variant="outlined"
                              sx={{
                                mt: 0.5,
                                fontSize: "0.7rem",
                                height: "18px",
                              }}
                            />
                          )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewRequest(request)}
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {(request.status === "pending" ||
                        request.status === "assigned") && (
                        <IconButton
                          size="small"
                          onClick={() => handleAssignWorker(request)}
                          title={
                            request.status === "assigned"
                              ? "Reassign Worker"
                              : "Assign Worker"
                          }
                          color="primary"
                        >
                          <AssignmentIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Request Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Request Details - {selectedRequest?.tokenNumber}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Category:</Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, textTransform: "capitalize" }}
                  >
                    {selectedRequest.category}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Priority:</Typography>
                  <Chip
                    label={selectedRequest.priority}
                    color={getPriorityColor(selectedRequest.priority)}
                    size="small"
                    sx={{ textTransform: "capitalize", mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Location:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedRequest.location}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Description:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedRequest.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Requester:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedRequest.requesterName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Contact:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {selectedRequest.contactPhone ||
                      selectedRequest.requesterEmail}
                  </Typography>
                </Grid>
                {selectedRequest.assignedWorkerName && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">
                        Assigned Worker:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedRequest.assignedWorkerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">
                        Worker Contact:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedRequest.assignedWorkerPhone ||
                          selectedRequest.assignedWorkerEmail}
                      </Typography>
                    </Grid>
                  </>
                )}
                {selectedRequest.adminNotes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Admin Notes:</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {selectedRequest.adminNotes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          {(selectedRequest?.status === "pending" ||
            selectedRequest?.status === "assigned") && (
            <Button
              onClick={() => {
                setViewDialog(false);
                handleAssignWorker(selectedRequest);
              }}
              variant="contained"
              color="primary"
            >
              {selectedRequest?.status === "assigned"
                ? "Reassign Worker"
                : "Assign Worker"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Assign Worker Dialog */}
      <Dialog
        open={assignDialog}
        onClose={() => setAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedRequest?.status === "assigned"
            ? "Reassign Worker"
            : "Assign Worker"}{" "}
          - {selectedRequest?.tokenNumber}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Show current assignment if reassigning */}
            {selectedRequest?.status === "assigned" &&
              selectedRequest?.assignedWorkerName && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Currently Assigned:</strong>{" "}
                    {selectedRequest.assignedWorkerName}
                    <br />
                    {selectedRequest.autoAssigned && (
                      <>
                        <strong>Assignment Type:</strong> Auto-assigned by
                        system
                        <br />
                      </>
                    )}
                    <strong>Note:</strong> Reassigning will override the current
                    assignment.
                  </Typography>
                </Alert>
              )}

            {/* Category and recommended specialization info */}
            {selectedRequest && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Request Category:</strong> {selectedRequest.category}
                  <br />
                  <strong>Subcategory:</strong>{" "}
                  {selectedRequest.subcategory || "N/A"}
                  <br />
                  <strong>Recommended specialists:</strong> Workers with{" "}
                  {selectedRequest.category} specialization
                </Typography>
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Worker</InputLabel>
              <Select
                value={assignmentForm.workerId}
                onChange={(e) =>
                  setAssignmentForm({
                    ...assignmentForm,
                    workerId: e.target.value,
                  })
                }
                label="Select Worker"
              >
                {workers
                  .sort((a, b) => {
                    // Sort workers by specialization match - matching specialists first
                    const requestCategory =
                      selectedRequest?.category?.toLowerCase();
                    if (!requestCategory) return 0;

                    const aSpecs = Array.isArray(a.specialization)
                      ? a.specialization
                      : [a.specialization];
                    const bSpecs = Array.isArray(b.specialization)
                      ? b.specialization
                      : [b.specialization];

                    const aMatches = aSpecs.some(
                      (spec) =>
                        spec?.toLowerCase().includes(requestCategory) ||
                        requestCategory.includes(spec?.toLowerCase())
                    );
                    const bMatches = bSpecs.some(
                      (spec) =>
                        spec?.toLowerCase().includes(requestCategory) ||
                        requestCategory.includes(spec?.toLowerCase())
                    );

                    if (aMatches && !bMatches) return -1;
                    if (!aMatches && bMatches) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((worker) => {
                    const requestCategory =
                      selectedRequest?.category?.toLowerCase();
                    const workerSpecs = Array.isArray(worker.specialization)
                      ? worker.specialization
                      : [worker.specialization];

                    const isRecommended =
                      requestCategory &&
                      workerSpecs.some(
                        (spec) =>
                          spec?.toLowerCase().includes(requestCategory) ||
                          requestCategory.includes(spec?.toLowerCase())
                      );

                    return (
                      <MenuItem key={worker.id} value={worker.id}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {worker.name}
                            </Typography>
                            {isRecommended && (
                              <Chip
                                label="Recommended"
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {worker.department}
                          </Typography>
                          <Typography variant="caption" color="primary.main">
                            Specialization:{" "}
                            {Array.isArray(worker.specialization)
                              ? worker.specialization.join(", ")
                              : worker.specialization || "General"}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Scheduled Date"
              type="datetime-local"
              value={assignmentForm.scheduledDate}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  scheduledDate: e.target.value,
                })
              }
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Estimated Duration (hours)"
              type="number"
              value={assignmentForm.estimatedDuration}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  estimatedDuration: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Admin Notes"
              multiline
              rows={3}
              value={assignmentForm.adminNotes}
              onChange={(e) =>
                setAssignmentForm({
                  ...assignmentForm,
                  adminNotes: e.target.value,
                })
              }
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitAssignment}
            variant="contained"
            color="primary"
            disabled={assignLoading || !assignmentForm.workerId}
          >
            {assignLoading
              ? selectedRequest?.status === "assigned"
                ? "Reassigning..."
                : "Assigning..."
              : selectedRequest?.status === "assigned"
              ? "Reassign Worker"
              : "Assign Worker"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminMaintenancePanel;
